/**
 * HistoryModal — historie verzí Codable projektu (Lovable parity).
 * Seznam commitů z codable.history (action=list) + restore + reject špatných verzí.
 */
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, GitBranch, RotateCcw, Rocket, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../lib/api';
import { Badge, Button, EmptyState, Modal } from '../../../components/ui';

interface Commit {
  sha: string;
  short: string;
  title: string;
  author: string;
  created_at: string;
  rejected?: boolean;
  rejected_reason?: string;
  rejected_via?: string;
}

interface ListResp {
  ok: boolean;
  commits?: Commit[];
  error?: string;
}

interface RestoreResp {
  ok: boolean;
  restore_commit?: string;
  restored_to?: string;
  short?: string;
  nothing_to_commit?: boolean;
  message?: string;
  runnable?: boolean;
  missing?: string[];
  preview?: { rebuilt?: boolean; mode?: string; visible_now?: string; ok?: boolean };
  error?: string;
}

interface RejectResp {
  ok: boolean;
  message?: string;
  error?: string;
}

/** Relativní čas — commity jsou typicky minuty/hodiny staré. */
function relTime(iso: string, tr: (k: string, opts?: Record<string, unknown>) => string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return iso;
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return tr('codable.historyJustNow');
  const m = Math.floor(s / 60);
  if (m < 60) return tr('codable.historyAgoMin', { n: m });
  const h = Math.floor(m / 60);
  if (h < 24) return tr('codable.historyAgoH', { n: h });
  const d = Math.floor(h / 24);
  if (d < 30) return tr('codable.historyAgoD', { n: d });
  return new Date(ts).toLocaleDateString();
}

export default function HistoryModal(
  { slug, onClose, onBack }: { slug: string; onClose: () => void; onBack?: () => void },
) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Inline confirm — první klik vybere commit, druhý teprve spustí restore.
  const [confirmSha, setConfirmSha] = useState<string | null>(null);
  // reject: null | sha (single) | `lineage:${sha}`
  const [rejectMode, setRejectMode] = useState<string | null>(null);
  // Výsledek přímo v modalu — platforma nemá popup toasty.
  const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['codable-history', slug],
    queryFn: () => api.mcp<ListResp>('codable.history', { slug, action: 'list' }),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['codable-history', slug] });
    queryClient.invalidateQueries({ queryKey: ['codable-project'] });
    queryClient.invalidateQueries({ queryKey: ['codable-tree', slug] });
    queryClient.invalidateQueries({ queryKey: ['codable-project-status', slug] });
  };

  const restore = useMutation({
    mutationFn: (sha: string) =>
      api.mcp<RestoreResp>('codable.history', {
        slug,
        action: 'restore',
        sha,
      }, { label: t('codable.historyRestoring', 'Obnovuji verzi na DEV…') }),
    onSuccess: (r) => {
      setConfirmSha(null);
      invalidate();
      if (!r.ok) {
        const text = r.error ?? t('codable.historyRestoreFail');
        setFeedback({ ok: false, text });
        toast.error(text);
        return;
      }
      // Prefer server message (includes empty-tree warning).
      const text = (r.message && r.message.trim()) || (() => {
        const shaShort = (r.restore_commit ?? r.short ?? r.restored_to ?? '').slice(0, 8);
        const previewNote = r.preview?.rebuilt
          ? t('codable.historyPreviewRebuilt', 'Preview přestavěn.')
          : r.preview?.mode === 'dev'
          ? t('codable.historyPreviewHmr', 'Preview HMR.')
          : (r.preview?.visible_now ?? '');
        return r.nothing_to_commit
          ? t('codable.historyAlreadyAt', { sha: shaShort, preview: previewNote })
          : t('codable.historyRestored', { sha: shaShort, preview: previewNote });
      })();
      const ok = r.runnable !== false;
      setFeedback({ ok, text });
      if (ok) toast.success(text);
      else toast.error(text);
      window.dispatchEvent(new CustomEvent('codable:preview-refresh', { detail: { slug } }));
    },
    onError: (e: unknown) => {
      setConfirmSha(null);
      const text = t('codable.errorGeneric', { msg: String((e as Error).message ?? e) });
      setFeedback({ ok: false, text });
      toast.error(text);
    },
  });

  const rejectMut = useMutation({
    mutationFn: (opts: { sha: string; lineage: boolean }) =>
      api.mcp<RejectResp>('codable.history', {
        slug,
        action: 'reject',
        sha: opts.sha,
        lineage: opts.lineage,
      }),
    onSuccess: (r) => {
      setRejectMode(null);
      invalidate();
      if (!r.ok) {
        const text = r.error ?? t('codable.historyRejectFail', 'Označení selhalo');
        setFeedback({ ok: false, text });
        toast.error(text);
        return;
      }
      const text = r.message ?? t('codable.historyRejected', 'Verze označena jako špatná');
      setFeedback({ ok: true, text });
      toast.success(text);
    },
    onError: (e: unknown) => {
      setRejectMode(null);
      const text = t('codable.errorGeneric', { msg: String((e as Error).message ?? e) });
      setFeedback({ ok: false, text });
      toast.error(text);
    },
  });

  const unrejectMut = useMutation({
    mutationFn: (sha: string) =>
      api.mcp<RejectResp>('codable.history', { slug, action: 'unreject', sha }),
    onSuccess: (r) => {
      invalidate();
      if (!r.ok) {
        const text = r.error ?? t('codable.historyRejectFail', 'Označení selhalo');
        setFeedback({ ok: false, text });
        return;
      }
      const text = r.message ?? t('codable.historyUnrejected', 'Označení zrušeno');
      setFeedback({ ok: true, text });
    },
    onError: (e: unknown) => {
      const text = t('codable.errorGeneric', { msg: String((e as Error).message ?? e) });
      setFeedback({ ok: false, text });
    },
  });

  const commits = data?.commits ?? [];
  const busy = restore.isPending || rejectMut.isPending || unrejectMut.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      onBack={onBack}
      backLabel={t('codable.wsSettings')}
      title={t('codable.historyTitle')}
      size='lg'
    >
      <div className='mb-3 rounded-xl border border-gray-800 bg-gray-900/55 p-3'>
        <div className='flex flex-wrap items-center gap-2'>
          <Badge variant='blue' className='gap-1'>
            <GitBranch size={12} />
            {t('codable.historyTargetDev')}
          </Badge>
          <Badge variant='gray' className='gap-1'>
            <Rocket size={12} />
            PROD
          </Badge>
        </div>
        <p className='mt-2 text-xs leading-relaxed text-gray-400'>
          {t('codable.historyRestoreHint')}
        </p>
        <p className='mt-1 text-xs leading-relaxed text-gray-500'>
          {t(
            'codable.historyRejectHint',
            'Špatná verze / řada se nepočítá do „Git má novější“ a v seznamu je označená.',
          )}
        </p>
      </div>
      {feedback && (
        <div
          role='status'
          className={`mb-3 rounded-xl border px-3 py-2 text-sm ${
            feedback.ok
              ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
              : 'border-red-500/40 bg-red-500/10 text-red-200'
          }`}
        >
          {feedback.text}
        </div>
      )}
      <div className='space-y-1 max-h-[60vh] overflow-y-auto -m-1 p-1'>
        {isLoading
          ? (
            <div className='space-y-2 py-2' aria-hidden='true'>
              {[...Array(5)].map((_, i) => (
                <div key={i} className='h-8 rounded-xl bg-gray-800 animate-pulse' />
              ))}
            </div>
          )
          : data?.ok === false
          ? <div className='text-sm text-red-400 py-2'>{data.error ?? t('codable.historyLoadError')}</div>
          : commits.length === 0
          ? <EmptyState title={t('codable.historyEmpty')} description={t('codable.historyEmptyDesc')} />
          : commits.map((c, i) => (
            <div
              key={c.sha}
              className={`flex items-center gap-3 py-2 px-2 rounded-xl hover:bg-gray-800/40 ${
                c.rejected ? 'opacity-60' : ''
              }`}
            >
              <span className='font-mono text-xs text-gray-500 flex-shrink-0'>{c.short}</span>
              <div className='flex-1 min-w-0'>
                <div className='flex flex-wrap items-center gap-1.5 min-w-0'>
                  <div className={`text-sm truncate ${c.rejected ? 'text-gray-400 line-through' : 'text-white'}`}>
                    {c.title}
                  </div>
                  {c.rejected && (
                    <Badge variant='gray' className='flex-shrink-0'>
                      {t('codable.historyBad', 'špatná')}
                    </Badge>
                  )}
                </div>
                <div className='text-[11px] text-gray-600 truncate'>
                  {c.author} · {relTime(c.created_at, t)}
                  {c.rejected_reason ? ` · ${c.rejected_reason}` : ''}
                </div>
              </div>
              <div className='flex flex-shrink-0 items-center gap-1'>
                {i === 0
                  ? <Badge variant='blue'>{t('codable.historyCurrent')}</Badge>
                  : (
                    <Button
                      size='sm'
                      variant={confirmSha === c.sha ? 'danger' : 'secondary'}
                      icon={<RotateCcw size={13} />}
                      loading={restore.isPending && restore.variables === c.sha}
                      disabled={busy}
                      onClick={() => {
                        if (confirmSha === c.sha) restore.mutate(c.sha);
                        else {
                          setConfirmSha(c.sha);
                          setRejectMode(null);
                        }
                      }}
                      title={confirmSha === c.sha
                        ? t('codable.historyConfirmTitle')
                        : t('codable.historyRestoreHint')}
                    >
                      {confirmSha === c.sha ? t('codable.historyConfirmDev') : t('codable.historyRestoreDev')}
                    </Button>
                  )}
                {c.rejected
                  ? (
                    <Button
                      size='sm'
                      variant='secondary'
                      icon={<Undo2 size={13} />}
                      loading={unrejectMut.isPending && unrejectMut.variables === c.sha}
                      disabled={busy}
                      onClick={() => unrejectMut.mutate(c.sha)}
                      title={t('codable.historyUnreject', 'Zrušit označení špatné')}
                    >
                      {t('codable.historyUnrejectShort', 'Zrušit')}
                    </Button>
                  )
                  : rejectMode === c.sha || rejectMode === `lineage:${c.sha}`
                  ? (
                    <>
                      <Button
                        size='sm'
                        variant='danger'
                        disabled={busy}
                        loading={rejectMut.isPending && rejectMut.variables?.sha === c.sha && !rejectMut.variables?.lineage}
                        onClick={() => rejectMut.mutate({ sha: c.sha, lineage: false })}
                        title={t('codable.historyRejectOne', 'Označit jen tuto verzi')}
                      >
                        {t('codable.historyRejectOneShort', 'Jen tato')}
                      </Button>
                      <Button
                        size='sm'
                        variant='danger'
                        disabled={busy}
                        loading={rejectMut.isPending && rejectMut.variables?.sha === c.sha && rejectMut.variables?.lineage}
                        onClick={() => rejectMut.mutate({ sha: c.sha, lineage: true })}
                        title={t('codable.historyRejectLineage', 'Tato + celá řada po ní')}
                      >
                        {t('codable.historyRejectLineageShort', 'Řada po ní')}
                      </Button>
                    </>
                  )
                  : (
                    <Button
                      size='sm'
                      variant='secondary'
                      icon={<Ban size={13} />}
                      disabled={busy}
                      onClick={() => {
                        setRejectMode(c.sha);
                        setConfirmSha(null);
                      }}
                      title={t('codable.historyReject', 'Označit jako špatnou')}
                    >
                      {t('codable.historyRejectShort', 'Špatná')}
                    </Button>
                  )}
              </div>
            </div>
          ))}
      </div>
    </Modal>
  );
}
